'use client';

import * as React from 'react';

type Theme = 'light' | 'dark' | 'system';
type FontSize = 'small' | 'medium' | 'large';

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  defaultFontSize?: FontSize;
  storageKey?: string;
  fontSizeStorageKey?: string;
  attribute?: string;
  enableSystem?: boolean;
  disableTransitionOnChange?: boolean;
};

type ThemeProviderState = {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  fontSize: FontSize;
  setFontSize: (fontSize: FontSize) => void;
};

const ThemeProviderContext = React.createContext<ThemeProviderState | undefined>(
  undefined
);

export function ThemeProvider({
  children,
  defaultTheme = 'system',
  defaultFontSize = 'medium',
  storageKey = 'pr-chat-theme',
  fontSizeStorageKey = 'pr-chat-font-size',
  attribute = 'class',
  enableSystem = true,
  disableTransitionOnChange = true,
  ...props
}: ThemeProviderProps) {
  const [theme, setTheme] = React.useState<Theme>(defaultTheme);
  const [fontSize, setFontSizeState] = React.useState<FontSize>(defaultFontSize);

  // Load theme and fontSize from localStorage on mount
  React.useEffect(() => {
    const root = window.document.documentElement;
    const storageTheme = localStorage.getItem(storageKey) as Theme | null;
    const storageFontSize = localStorage.getItem(fontSizeStorageKey) as FontSize | null;

    if (storageTheme) {
      setTheme(storageTheme);
    }

    if (storageFontSize) {
      setFontSizeState(storageFontSize);
    }
  }, [storageKey, fontSizeStorageKey]);

  // Apply theme to document root
  React.useEffect(() => {
    const root = window.document.documentElement;

    if (disableTransitionOnChange) {
      root.classList.add('[&_*]:!transition-none');
      window.setTimeout(() => {
        root.classList.remove('[&_*]:!transition-none');
      }, 0);
    }

    root.classList.remove('light', 'dark');

    if (theme === 'system' && enableSystem) {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
      root.classList.add(systemTheme);
      return;
    }

    root.classList.add(theme);
  }, [theme, enableSystem, disableTransitionOnChange]);

  // Apply fontSize to document root
  React.useEffect(() => {
    const root = window.document.documentElement;

    // Remove all font-size classes
    root.classList.remove('font-size-small', 'font-size-medium', 'font-size-large');

    // Add the current fontSize class
    root.classList.add(`font-size-${fontSize}`);
  }, [fontSize]);

  const value = React.useMemo(
    () => ({
      theme,
      setTheme: (newTheme: Theme) => {
        localStorage.setItem(storageKey, newTheme);
        setTheme(newTheme);
      },
      fontSize,
      setFontSize: (newFontSize: FontSize) => {
        localStorage.setItem(fontSizeStorageKey, newFontSize);
        setFontSizeState(newFontSize);
      },
    }),
    [theme, fontSize, storageKey, fontSizeStorageKey]
  );

  return (
    <ThemeProviderContext.Provider {...props} value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = React.useContext(ThemeProviderContext);

  if (context === undefined)
    throw new Error('useTheme must be used within a ThemeProvider');

  return context;
};
